/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Card from "@mui/material/Card";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Vision UI Dashboard React example components
import LineChart from "examples/Charts/LineCharts/LineChart2";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import React, { useEffect, useState, useCallback } from "react"

import { useHistory } from 'react-router-dom';
import { getUser } from 'discordAPI'
import { getGuildRows } from 'tableHelper'
import { getAPIToken, getGuilds } from 'GaiaAPI'
import { getDetailsFromHistoryCode } from 'Utils'

function Alliance() {
    const history = useHistory();
    const [guildRows, setGuildRows] = useState([]);

    const [lineChartData, setLineChartData] = useState([]);
    const [lineChartOptions, setLineChartOptions] = useState([]);

    const guildColumns = [
        { name: "pos", align: "left" },
        { name: "name", align: "left" },
        { name: "members", align: "center" },
        { name: "power", align: "left" },
        { name: "power inc", align: "center" },
        { name: "ace", align: "center" },
        { name: "se score", align: "center" },
        { name: "requirements", align: "center" }
    ]

    function getPowerFromHistory(historyCode) {
        var power = null;
        let history = getDetailsFromHistoryCode(historyCode)
        if (history && history.power) {
            power = history.power.toFixed(2);
        }

        return power;
    }

    function setupLineChart(guilds) {

        let rows = [];
        let months = [];
        let colours = [];

        try {
            if (guilds) {
                for (let i = 0; i < guilds.length; i++) {

                    let guild = guilds[i];

                    if (guild.members > 0) {
                        if (guild && guild.colour)
                            colours.push('#' + guild.colour);
                        if (months.length === 0) {
                            for (let i2 = guild.historyDates.length - 1; i2 >= 0; i2--) {
                                months.push(guild.historyDates[i2]);
                            }
                            months.push('Now');
                        }

                        let power1 = guild.power.toFixed(2);
                        let power2 = getPowerFromHistory(guild.history[0]);
                        let power3 = getPowerFromHistory(guild.history[1]);
                        let power4 = getPowerFromHistory(guild.history[2]);
                        let power5 = getPowerFromHistory(guild.history[3]);
                        let power6 = getPowerFromHistory(guild.history[4]);
                        let power7 = getPowerFromHistory(guild.history[5]);
                        let power8 = getPowerFromHistory(guild.history[6]);
                        let power9 = getPowerFromHistory(guild.history[7]);
                        let power10 = getPowerFromHistory(guild.history[8]);
                        let power11 = getPowerFromHistory(guild.history[9]);
                        let power12 = getPowerFromHistory(guild.history[10]);

                        let data = [power12, power11, power10, power9, power8, power7, power6, power5, power4, power3, power2, power1];

                        rows.push({
                            name: guild.name,
                            data: data
                        });
                    }
                }
            }
        }
        catch (err) {
            alert(err);
        }

        let options = {
            chart: {
                toolbar: {
                    show: false,
                },
            },
            tooltip: {
                theme: "dark",
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: "smooth",
            },
            xaxis: {
                type: "datetime",
                categories: months,
                labels: {
                    style: {
                        colors: "#c8cfca",
                        fontSize: "10px",
                    },
                },
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: "#c8cfca",
                        fontSize: "10px",
                    },
                },
            },
            legend: {
                show: false,
            },
            grid: {
                strokeDashArray: 5,
                borderColor: "#56577A",
            },
            fill: {
                type: "gradient",
                gradient: {
                    shade: "dark",
                    type: "vertical",
                    shadeIntensity: 0,
                    gradientToColors: undefined, // optional, if not defined - uses the shades of same color in series
                    inverseColors: true,
                    opacityFrom: 0.8,
                    opacityTo: 0,
                    stops: [],
                },
                colors: colours,
            },
            colors: colours, //["#0075FF", "#2CD9FF"],
        }

        setLineChartOptions(options);
        setLineChartData(rows);
    }

    const getDetails = useCallback(async () => {
        getUser()
            .then((result) => {
                getAPIToken()
                    .then((token) => {
                        getGuilds(token)
                            .then((guildsResult) => {
                                setGuildRows(getGuildRows(guildColumns, guildsResult));
                                setupLineChart(guildsResult);
                            },
                            (error) => {
                                alert(error);
                            });
                    },
                    (error) => {
                        alert(error);
                    });
            },
            (error) => {
                history.push('/authentication/sign-in');
            });
    }, []);


    useEffect(() => {
        getDetails();
    }, [getDetails]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <VuiBox py={3}>
                <VuiBox mb={3}>
                    <Card>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px">
                            <VuiTypography variant="lg" color="white">
                                Alliance
                            </VuiTypography>
                        </VuiBox>
                        <VuiBox
                            sx={{
                                "& th": {
                                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                                        `${borderWidth[1]} solid ${grey[700]}`,
                                },
                                "& tr": {
                                    fontSize: 12,
                                },
                                "& .MuiTableRow-root:not(:last-child)": {
                                    "& td": {
                                        borderBottom: ({
                                            borders: { borderWidth }, palette: { grey } }) =>
                                            `${borderWidth[1]} solid ${grey[700]}`,
                                    },
                                },
                            }}
                        >
                            <Table columns={guildColumns} rows={guildRows} />
                        </VuiBox>
                    </Card>
                </VuiBox>
                <VuiBox mb={3}>
                    <Card>
                        <VuiBox sx={{ height: "100%" }}>
                            <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                                History
                            </VuiTypography>
                            <VuiBox sx={{ height: "510px" }}>
                                <LineChart
                                    lineChartData={lineChartData}
                                    lineChartOptions={lineChartOptions}
                                />
                            </VuiBox>
                        </VuiBox>
                    </Card>
                </VuiBox>
            </VuiBox>
            <Footer />
        </DashboardLayout>
    );
}

export default Alliance;
